import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, MessageCircle, Phone, Mail, X, Loader2, Sparkles } from "lucide-react";
import { CONTACT, SERVICES, serviceTitle, whatsappLink } from "@/lib/site";
import { streamAI } from "@/lib/ai";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", email: "", zone: "", property_type: "", message: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{label}</span>
    {children}
  </label>
);

export const QuoteForm = ({ selected, onToggle, onClear }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [writing, setWriting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const compose = async () => {
    setWriting(true);
    const body = { services: selected, property_type: form.property_type, zone: form.zone, notes: form.message };
    try {
      await streamAI("/ai/compose", body, (t) => setForm((f) => ({ ...f, message: t })));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setWriting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected.length) return toast.error("Scegli almeno un servizio dal menu.");
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, services: selected.map(serviceTitle) });
      toast.success("Richiesta inviata! Ti ricontatteremo al più presto.");
      setForm(EMPTY);
      onClear();
    } catch {
      toast.error("Invio non riuscito. Riprova o scrivici su WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" className="relative border-t border-white/10 bg-[#05080F] py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.2fr]">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.8 }}>
          <p className="eyebrow">Preventivo gratuito</p>
          <h2 className="mt-5 font-display text-4xl font-bold leading-[1] tracking-tight text-white sm:text-5xl">Raccontaci il tuo spazio.</h2>
          <p className="mt-5 max-w-md text-base text-slate-300">Ti rispondiamo con una proposta su misura. Preferisci parlare subito? Usa WhatsApp o chiamaci.</p>
          <div className="mt-10 space-y-3">
            <a data-testid="whatsapp-direct-button" href={whatsappLink(selected)} target="_blank" rel="noopener noreferrer" className="contact-row">
              <MessageCircle className="h-5 w-5 text-[#25D366]" /> <span>WhatsApp {selected.length > 0 && `· con ${selected.length} servizi`}</span>
            </a>
            <a data-testid="contact-phone" href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="contact-row"><Phone className="h-5 w-5 text-[var(--cyan)]" /> {CONTACT.phone}</a>
            <a data-testid="contact-email" href={`mailto:${CONTACT.email}`} className="contact-row"><Mail className="h-5 w-5 text-[var(--cyan)]" /> {CONTACT.email}</a>
          </div>
        </motion.div>

        <motion.form onSubmit={submit} data-testid="quote-form" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 0.8, delay: 0.1 }}
          className="rounded-[28px] border border-white/10 bg-[#0A1020] p-6 sm:p-10">
          <Field label="Servizi scelti">
            <div className="flex min-h-[52px] flex-wrap gap-2" data-testid="quote-form-services">
              {selected.length === 0 && <span className="text-sm text-slate-400">Nessun servizio: aggiungili qui sotto o dal menu.</span>}
              {selected.map((id) => (
                <button type="button" key={id} onClick={() => onToggle(id)} data-testid={`quote-chip-${id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--cyan)] px-3 py-1.5 text-sm font-semibold text-black">
                  {serviceTitle(id)} <X className="h-3.5 w-3.5" />
                </button>
              ))}
            </div>
            <select data-testid="quote-form-add-service" value="" onChange={(e) => e.target.value && onToggle(e.target.value)} className="input-dark mt-3">
              <option value="">+ Aggiungi un servizio</option>
              {SERVICES.filter((s) => !selected.includes(s.id)).map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
          </Field>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <Field label="Nome e cognome / Azienda"><input required data-testid="quote-form-name" value={form.name} onChange={set("name")} className="input-dark" /></Field>
            <Field label="Telefono"><input required type="tel" data-testid="quote-form-phone" value={form.phone} onChange={set("phone")} className="input-dark" /></Field>
            <Field label="Email (facoltativa)"><input type="email" data-testid="quote-form-email" value={form.email} onChange={set("email")} className="input-dark" /></Field>
            <Field label="Comune / Zona"><input required data-testid="quote-form-zone" value={form.zone} onChange={set("zone")} placeholder="es. Milano, Sesto S.G." className="input-dark" /></Field>
          </div>
          <div className="mt-5">
            <Field label="Tipo di immobile">
              <select data-testid="quote-form-property" value={form.property_type} onChange={set("property_type")} className="input-dark">
                <option value="">Seleziona…</option>
                {["Abitazione", "Ufficio / Studio", "Condominio", "Negozio", "Capannone / Magazzino", "Stabilimento produttivo", "Altro"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </Field>
          </div>
          <div className="mt-5">
            <div className="mb-2 flex items-center justify-between gap-3">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Messaggio</span>
              <button type="button" onClick={compose} disabled={writing} data-testid="quote-form-ai-compose" className="inline-flex items-center gap-1.5 rounded-full border border-[var(--cyan)]/50 px-3 py-1.5 text-xs font-semibold text-[var(--cyan)] transition-colors hover:bg-[var(--cyan)]/10 disabled:opacity-60">
                {writing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Scrivi con l'AI
              </button>
            </div>
            <textarea rows={5} aria-label="Messaggio" data-testid="quote-form-message" value={form.message} onChange={set("message")} placeholder="Metri quadri, frequenza, orari preferiti… oppure scrivi due parole e premi «Scrivi con l'AI»" className="input-dark resize-none" />
          </div>
          <button type="submit" disabled={sending} data-testid="quote-form-submit-button" className="btn-cyan mt-8 w-full justify-center !py-4 text-base disabled:opacity-60">
            {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} Invia richiesta
          </button>
        </motion.form>
      </div>
    </section>
  );
};
